import { useFormik } from 'formik';
import * as Yup from 'yup';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import React, { useState, useRef } from 'react';
import Card from '@mui/material/Card';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import * as Styled from './userProfile.styles';
import { pricing } from 'oems/pricing';
import MDButton from 'components/MDButton';
import { Button, Input } from 'components/ui';
import { NewPasswordModal } from './components';
import ToastMessage from 'components/toastMessage';
import { useChangeUsername } from 'api';
import { selectUsername, doGetAuthenticatedUserAsync } from 'features/logon/logonSlice';
import { useSelector, useDispatch } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SecurityIcon from '@mui/icons-material/Security';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { useGetUserPurchasePlan } from 'api';
import { lazy } from 'react';
import { Suspense } from 'react';

const schema = Yup.object({
  newUsername: Yup.string().required('Pole wymagane'),
});

export function UserProfile() {
  const dispatch = useDispatch();
  const pricePlanRef = useRef(null);
  const securityRef = useRef(null);
  const userSettingsRef = useRef(null);
  const username = useSelector(selectUsername);
  const [usernameValue, setUsernameValue] = useState(username);
  const [editPassword, setEditPassword] = useState(null);
  const [toastData, setToastData] = useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const { data: purchasedPlan } = useGetUserPurchasePlan();

  React.useEffect(() => {
    setFieldValue('newUsername', usernameValue);
  }, [usernameValue]);

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      newUsername: usernameValue || '',
    },
    validationSchema: schema,
    validateOnChange: false,
    onSubmit: (values) => {
      setIsSaving(true);
      updateUsername(schema.cast(values));
    },
  });

  const { mutate: updateUsername, isLoading: isUpdatingUsername } = useChangeUsername({
    onSuccess: () => {
      setFieldValue('');
      setToastData({ severity: 'success', message: 'Zapisano nazwę użytkownika' });
      setIsSaving(false);
      dispatch(doGetAuthenticatedUserAsync());
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się zapisać nazwy użytkownika' });
      setIsSaving(false);
    },
  });

  const UserProfilePricing = lazy(() => import(`oems/`+process.env.REACT_APP_OEM_NAME+`/src/components/UserProfilePricing/UserProfilePricing`));

  return (
    <DashboardLayout>
      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}
      <DashboardNavbar />

      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="flex-start" gap={2}>
        {/* col 1 */}
        <Stack direction="column" justifyContent="flex-start">
          <Styled.Menu>
            <Button
              variant="text"
              color="secondary"
              className="menuItem"
              onClick={() => userSettingsRef.current.scrollIntoView({ behavior: 'smooth' })}
            >
              <AccountCircleIcon sx={{ mr: 1 }} />
              Dane użytkownika
            </Button>
            <Button
              variant="text"
              color="secondary"
              className="menuItem"
              onClick={() => securityRef.current.scrollIntoView({ behavior: 'smooth' })}
            >
              <SecurityIcon sx={{ mr: 1 }} />
              Bezpieczeństwo
            </Button>
            <Button
              variant="text"
              color="secondary"
              className="menuItem"
              onClick={() => pricePlanRef.current.scrollIntoView({ behavior: 'smooth' })}
            >
              <CardMembershipIcon sx={{ mr: 1 }} />
              Plan cenowy
            </Button>
          </Styled.Menu>
        </Stack>
        {/* col 2 */}
        <Stack direction="column" justifyContent="flex-start" gap={2}>
          <Card ref={userSettingsRef} sx={{ display: 'flex', px: 3, py: 2 }}>
            <Stack direction="column" sx={{ p: 1 }} justifyContent="flex-start">
              <Styled.BillzTitle>Dane użytkownika</Styled.BillzTitle>
              <Stack flexGrow={1} direction="column" justifyContent="left">
                <Styled.Wrapper>
                  <Stack direction="column">
                    <Styled.h3h>Jak możemy się do Ciebie zwracać?</Styled.h3h>
                    <Styled.HiddenDivider />
                    <Stack direction="row">
                      <Input onChange={setUsernameValue} value={usernameValue} label="Nazwa użytkownika" />
                      <Styled.Grid item xs={10} spacing={0}>
                        <MDButton color="info" onClick={handleSubmit} loading={isSaving}>
                          Zapisz
                        </MDButton>
                      </Styled.Grid>
                    </Stack>
                  </Stack>
                </Styled.Wrapper>
              </Stack>
            </Stack>
          </Card>
          <Card ref={securityRef} sx={{ display: 'flex', px: 3, py: 2 }}>
            <Stack direction="column" sx={{ p: 1 }} justifyContent="flex-start">
              <Styled.BillzTitle>Bezpieczeństwo</Styled.BillzTitle>
              <Stack flexGrow={1} direction="column" justifyContent="left">
                <Styled.Wrapper>
                  <Stack direction="column" justifyContent="left">
                    <Styled.h3h>Hasło</Styled.h3h>
                    <Styled.HiddenDivider />
                    <Styled.h6>Zmień hasło zabezpiczające dostęp do twojego konta</Styled.h6>
                    <Styled.HiddenDivider />
                    <Box>
                      <MDButton color="info" variant="outlined" onClick={() => setEditPassword({})}>
                        Zmień hasło
                      </MDButton>
                    </Box>
                    {editPassword && (
                      <NewPasswordModal
                        data={editPassword}
                        onClose={() => setEditPassword(null)}
                        onSuccess={() => setToastData({ severity: 'success', message: 'Zapisano hasło' })}
                      />
                    )}
                  </Stack>
                </Styled.Wrapper>
              </Stack>
            </Stack>
          </Card>
          <Card ref={pricePlanRef} sx={{ display: 'flex', px: 3, py: 1 }}>
            <Stack direction="column" sx={{ p: 1 }} justifyContent="flex-start">
              <Styled.BillzTitle>Plan cenowy</Styled.BillzTitle>
              <Suspense fallback={<div>Loading...</div>}>
                <UserProfilePricing 
                  pricing={pricing}
                  purchasedPlan={purchasedPlan}
                />
              </Suspense>
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </DashboardLayout>
  );
}

export default UserProfile;
